<script setup lang="ts">
import { StepAnswer, MaskInput, MASK_INPUT_FIELD_TYPE } from '@models/quiz';
import { vMaska } from 'maska';
import { ref, computed, onMounted } from 'vue';

type Props = {
  content: MaskInput
  modelValue: StepAnswer<MaskInput> | undefined
  columns?: string
}

const props = withDefaults(defineProps<Props>(), {
  columns: '4',
});

const emit = defineEmits<{ 'update:modelValue': [value: string], 'submit': [] }>();

const inputRef = ref<HTMLInputElement | null>(null);

const colClass = computed(() => `sm:f-col-${props.columns}`);
const template = computed(() => props.content.mask?.match(/{.+}/g)?.[0]);
const placeholder = computed(() => template.value?.slice(1, template.value.length - 1));

const textArray = computed(() => {
  return template.value ? props.content.mask?.split(template.value) : props.content.mask;
});

const model = computed({
  get() {
    return props.modelValue?.value ?? '';
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const heightMaskOptions = {
  mask: (value: string) => {
    if (value.length <= 4) {
      setTimeout(() => {
        inputRef.value?.setSelectionRange(4, 4);
      }, 0);

      return 'A\' B"';
    }

    return 'A\' BC"';
  },
};

onMounted(() => {
  const inputs = document.querySelectorAll('.mask-input-answer__field');
  const order = Array.from(inputs).indexOf(inputRef.value as HTMLElement);

  if (order === 0) {
    inputRef.value?.focus();
  }
});

const submit = () => {
  emit('submit');
};
</script>

<template>
  <div
    class="mask-input-answer f-col-full inline-block"
    :class="colClass"
  >
    <label class="flex flex-wrap text-20 font-bold sm:text-30">
      <span
        v-if="textArray?.[0]"
        class="mr-[0.3em]"
      >
        {{ textArray?.[0] }}
      </span>

      <span class="relative mr-[0.3em]">
        <span
          class="invisible"
          :class="{ 'pr-[0.3em]': content.fieldType === MASK_INPUT_FIELD_TYPE.HEIGHT }"
        >
          {{ model || placeholder }}
        </span>

        <input
          v-if="content.fieldType === MASK_INPUT_FIELD_TYPE.TEXT"
          ref="inputRef"
          v-model="model"
          class="mask-input-answer__field"
          type="text"
          :placeholder="placeholder"
          spellcheck="false"
          @keydown.enter="submit"
        >
        <input
          v-else-if="content.fieldType === MASK_INPUT_FIELD_TYPE.NUMBER"
          ref="inputRef"
          v-model="model"
          v-maska
          data-maska="ABB"
          data-maska-tokens="{'A': { 'pattern': '[1-9]' }, 'B': { 'pattern': '[0-9]' }}"
          class="mask-input-answer__field"
          type="text"
          :placeholder="placeholder"
          spellcheck="false"
          @keydown.enter="submit"
        >
        <input
          v-else-if="content.fieldType === MASK_INPUT_FIELD_TYPE.HEIGHT"
          ref="inputRef"
          v-model="model"
          v-maska:[heightMaskOptions]
          data-maska-tokens="{ 'A': { 'pattern': '[1-9]' }, 'B': { 'pattern': '[0-9]' }, 'C': { 'pattern': '[0-9]', 'optional': true }}"
          data-maska-eager
          class="mask-input-answer__field"
          type="text"
          :placeholder="placeholder"
          spellcheck="false"
          @keydown.enter="submit"
        >

        {{ textArray?.[1] }}
      </span>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.mask-input-answer__field {
  @apply
    absolute
    top-0
    left-0
    w-full
    h-full
    outline-none
    bg-transparent
    text-20
    sm:text-30
    font-medium
    placeholder:text-black-30;
}
</style>
